import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function AboutPage() {
  const [activeSection, setActiveSection] = useState('mission');

  const values = [
    {
      title: 'Accessibility',
      description: 'Mental health care should be easy to find and accessible to everyone, regardless of location or circumstance.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      title: 'Transparency',
      description: 'Clear, honest information about therapists, credentials, and treatment approaches helps you make informed decisions.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )
    },
    {
      title: 'Quality',
      description: 'Every therapist on our platform is licensed, verified, and committed to providing excellent mental health care.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  return (
    <>
      <SEOHead {...seoConfigs.about} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Hero Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#254e70] mb-6">
              Making therapy accessible for everyone
            </h1>
            <p className="text-xl text-[#254e70]/80 leading-relaxed max-w-3xl mx-auto">
              We're building a better way to find and connect with licensed therapists. 
              Simple, transparent, and focused on helping you get the care you need.
            </p>
          </div>
        </section>

        {/* Mission & Values Section */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Navigation */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-white rounded-lg p-1 border border-gray-200">
                {[
                  { id: 'mission', label: 'Mission' },
                  { id: 'values', label: 'Values' },
                  { id: 'story', label: 'Story' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSection(tab.id)}
                    className={`px-6 py-2 rounded-md font-medium transition-colors ${
                      activeSection === tab.id
                        ? 'bg-[#254e70] text-white'
                        : 'text-[#254e70]/70 hover:text-[#254e70]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-2xl p-8 lg:p-12 border border-gray-100">
              {activeSection === 'mission' && (
                <div className="text-center max-w-3xl mx-auto">
                  <h2 className="text-3xl font-semibold text-[#254e70] mb-6">
                    Our Mission
                  </h2>
                  <p className="text-lg text-[#254e70]/80 leading-relaxed">
                    To simplify the process of finding quality mental health care. We believe that everyone deserves access to the right therapist, and that the search process should be straightforward, transparent, and empowering.
                  </p>
                </div>
              )}

              {activeSection === 'values' && (
                <div>
                  <h2 className="text-3xl font-semibold text-[#254e70] mb-10 text-center">
                    Our Values
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {values.map((value, index) => (
                      <div key={index} className="text-center">
                        <div className="w-12 h-12 bg-[#9dacff] rounded-lg flex items-center justify-center text-white mb-4 mx-auto">
                          {value.icon}
                        </div>
                        <h3 className="text-xl font-semibold text-[#254e70] mb-3">
                          {value.title}
                        </h3>
                        <p className="text-[#254e70]/70 leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeSection === 'story' && (
                <div className="text-center max-w-3xl mx-auto">
                  <h2 className="text-3xl font-semibold text-[#254e70] mb-6">
                    Our Story
                  </h2>
                  <p className="text-lg text-[#254e70]/80 leading-relaxed">
                    TherapyList started from a simple frustration: why is it so hard to find the right therapist? We realized that people need clear, honest information to make confident decisions about their mental health care. So we built a platform that prioritizes transparency and makes the search process actually helpful.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Image Side */}
                <div className="bg-[#9dacff] p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-56 h-56 rounded-full overflow-hidden mb-6 mx-auto border-4 border-white/20">
                      <img 
                        src="https://counselingunconditionally.com/wp-content/uploads/2025/02/Courtney-Headshot-4-1.jpg"
                        alt="Courtney Morgan, LPCC-S, Co-Founder of TherapyList"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-2">
                      Courtney Morgan
                    </h3>
                    <p className="text-white/90">
                      LPCC-S, Co-Founder
                    </p>
                  </div>
                </div>

                {/* Content Side */}
                <div className="p-8 lg:p-12">
                  <h2 className="text-3xl font-semibold text-[#254e70] mb-6">
                    Meet Our Co-Founder
                  </h2>
                  <p className="text-[#9dacff] font-medium mb-6">
                    Licensed Professional Clinical Counselor Supervisor
                  </p>

                  <div className="space-y-4 text-[#254e70]/80 leading-relaxed mb-8">
                    <p>
                      As a practicing therapist, I saw firsthand how difficult it was for people to find the right mental health care. Clients would often tell me they'd spent weeks searching through confusing directories and unclear listings before finding my practice.
                    </p>
                    
                    <p>
                      I started TherapyList because I believe the process of finding a therapist should be as straightforward as the therapy itself. People deserve clear information, honest profiles, and an easy way to connect with qualified professionals who are the right fit for their needs.
                    </p>
                    
                    <p>
                      My goal is to remove the barriers that keep people from getting the mental health support they deserve.
                    </p>
                  </div>

                  <div className="p-4 bg-[#e4fde1] rounded-lg">
                    <p className="text-[#254e70]/70 italic">
                      "I wanted to create the resource I wish had existed when I was starting my practice—and the one I wish my clients had when they were looking for help."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-2xl p-8 lg:p-12 border border-gray-100">
              <h2 className="text-3xl font-semibold text-[#254e70] mb-4">
                Ready to find your therapist?
              </h2>
              <p className="text-lg text-[#254e70]/80 mb-8">
                Browse qualified therapists in your area and find the right fit for your needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/find-therapists"
                  className="bg-[#254e70] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#1d3e5a] transition-colors inline-block text-center"
                >
                  Browse Therapists
                </Link>
                <Link 
                  to="/how-it-works"
                  className="bg-[#9dacff] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#8a9cf5] transition-colors inline-block text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default AboutPage;